import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { TagProps, TagSize, TagStyles, TagVariant } from './Tag.types';
import { TypographyStyle } from 'src/packages/core/text';
import { Margin } from 'src/packages/core';

export const getStyles = (
  theme: DefaultTheme,
  variant: TagVariant,
  size: TagSize,
  color?: string,
  textColor?: string,
  rounded?: boolean
): TagStyles => {
  const getTypographyStyles = (): TypographyStyle => {
    let fontSize;
    let lineHeight;
    const fontWeight = 400;
    const fontFamily = 'Inter-Medium';

    if (size === 'default') {
      fontSize = '12px';
      lineHeight = '14px';
    } else {
      fontSize = '14px';
      lineHeight = '22px';
    }

    return {
      fontSize,
      lineHeight,
      fontWeight,
      fontFamily,
    };
  };

  const getOutlinedStyles = (color?: string, textColor?: string, rounded?: boolean): TagStyles => {
    return {
      backgroundColor: 'transparent',
      border: `1px solid ${color ?? theme.colors.border.darker}`,
      borderRadius: `${rounded ? '16px' : '4px'}`,
      color: `${textColor ?? theme.colors.text.primary}`,
      padding: `${size === 'default' ? '0 8px' : '0 12px'}`,
      height: `${size === 'default' ? '24px' : '32px'}`,
      ...getTypographyStyles(),
    };
  };

  const getFilledStyles = (color?: string, textColor?: string, rounded?: boolean): TagStyles => {
    return {
      backgroundColor: `${color ?? theme.colors.background.secondary.BG4}`,
      border: 'none',
      borderRadius: `${rounded ? '16px' : '4px'}`,
      color: `${textColor ?? theme.colors.text.primary}`,
      padding: `${size === 'default' ? '0 8px' : '0 12px'}`,
      height: `${size === 'default' ? '24px' : '32px'}`,
      ...getTypographyStyles(),
    };
  };

  const styles = {
    outlined: getOutlinedStyles(color, textColor, rounded),
    filled: getFilledStyles(color, textColor, rounded),
  };

  return styles[variant];
};

export const StyledTag = styled((props) => <Margin {...props} />)<TagProps & { styles: TagStyles }>`
  display: flex;
  justify-content: center;
  align-items: center;
  outline: 0;
  position: relative;
  width: fit-content;
  min-width: unset;
  direction: ltr;

  ${({ styles, clickable }) => `
    cursor: ${clickable ? 'pointer' : 'text'} !important;
    border-radius: ${styles.borderRadius};
    background-color: ${styles.backgroundColor};
    color: ${styles.color};
    border: ${styles.border};
    padding: ${styles.padding};
    height: ${styles.height};

    > span {
      unicode-bidi: bidi-override;
      font-size: ${styles.fontSize};
      font-weight: ${styles.fontWeight};
      line-height: ${styles.lineHeight};
      font-family: ${styles.fontFamily};
    }
  `};
`;

export const RightIconWrapper = styled.div<{ hasMargin: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${(props) => (props.hasMargin ? '4px' : 'unset')};
`;

export const LeftIconWrapper = styled.div<{ hasMargin: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${(props) => (props.hasMargin ? '4px' : 'unset')};
`;
